/**
 * AdminMessagesTab.jsx — admin-only tab inside the Admin Dashboard for:
 *   1) Sending a broadcast to users / scribes / all
 *   2) Picking a user/writer and opening a 1:1 DM (which deep-links to the
 *      shared /inbox page so admins use the same conversation UI as everyone
 *      else — no duplicated chat panel to maintain).
 *
 * Reuses backend endpoints from messaging_routes.py.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Send, Megaphone, Image as ImageIcon, Loader2, Search, MessageSquare,
} from 'lucide-react';
import { toast } from 'sonner';
import { authFetch } from '../utils/authUtils';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Button } from './ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminMessagesTab = () => {
  const navigate = useNavigate();
  const [audience, setAudience] = useState('all');
  const [body, setBody] = useState('');
  const [pendingImage, setPendingImage] = useState(null);
  const [sending, setSending] = useState(false);
  const fileRef = useRef(null);

  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);

  const [recentBroadcasts, setRecentBroadcasts] = useState([]);

  // -- Load recent broadcasts so admins can see what's already gone out -
  const fetchRecent = async () => {
    try {
      const r = await authFetch(`${BACKEND_URL}/api/messaging/broadcasts`);
      if (r.ok) {
        const d = await r.json();
        setRecentBroadcasts((d.broadcasts || []).slice(0, 5));
      }
    } catch (_) { /* silent */ }
  };
  useEffect(() => { fetchRecent(); }, []);

  // -- Debounced recipient search --------------------------------------
  useEffect(() => {
    if (!search.trim()) {
      setSearchResults([]);
      return undefined;
    }
    setSearchLoading(true);
    const t = setTimeout(async () => {
      try {
        const r = await authFetch(`${BACKEND_URL}/api/messaging/recipients?q=${encodeURIComponent(search.trim())}`);
        const d = await r.json();
        if (r.ok) setSearchResults(d.recipients || []);
      } finally {
        setSearchLoading(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [search]);

  // -- Image attach ----------------------------------------------------
  const onPickImage = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    if (!f.type.startsWith('image/')) {
      toast.error('Please choose an image');
      return;
    }
    if (f.size > 5 * 1024 * 1024) {
      toast.error('Image too large (max 5MB)');
      return;
    }
    const form = new FormData();
    form.append('image', f);
    try {
      const r = await authFetch(`${BACKEND_URL}/api/messaging/upload-image`, { method: 'POST', body: form });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || `Upload failed (${r.status})`);
      setPendingImage({ url: d.url });
    } catch (err) {
      toast.error(err.message || 'Image upload failed');
    }
  };

  const sendBroadcast = async () => {
    const text = body.trim();
    const image_url = pendingImage?.url || null;
    if (!text && !image_url) {
      toast.error('Add a message or image first');
      return;
    }
    setSending(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/api/messaging/broadcasts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ audience, body: text, image_url }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || `Send failed (${r.status})`);
      toast.success(`Broadcast sent to ${audience}`);
      setBody('');
      setPendingImage(null);
      fetchRecent();
    } catch (err) {
      toast.error(err.message || 'Broadcast failed');
    } finally {
      setSending(false);
    }
  };

  const openDMWith = (recipient) => {
    // The Inbox page knows how to seed a fresh thread when there are no
    // messages yet — we just navigate there with a deep-link hint.
    navigate(`/inbox?dm=${encodeURIComponent(recipient.user_id)}`);
  };

  const audiencePills = useMemo(() => ([
    { key: 'all',     label: 'Everyone',   color: 'bg-indigo-600 hover:bg-indigo-700' },
    { key: 'users',   label: 'Readers',    color: 'bg-emerald-600 hover:bg-emerald-700' },
    { key: 'writers', label: 'Scribes',    color: 'bg-purple-600 hover:bg-purple-700' },
  ]), []);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-4" data-testid="admin-messages-tab-content">
      {/* Broadcast composer */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Megaphone className="w-5 h-5 text-pink-400" />
            Broadcast announcement
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs uppercase tracking-wider font-bold text-slate-400">Audience:</span>
            {audiencePills.map((p) => (
              <button
                key={p.key}
                type="button"
                onClick={() => setAudience(p.key)}
                className={`px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${
                  audience === p.key ? `${p.color} text-white shadow` : 'bg-slate-700/60 text-slate-300 hover:bg-slate-700'
                }`}
                data-testid={`broadcast-audience-${p.key}`}
              >
                {p.label}
              </button>
            ))}
          </div>

          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value.slice(0, 4000))}
            rows={5}
            placeholder="What do you want to tell them? (max 4000 chars)"
            className="w-full rounded-lg bg-slate-900 border border-slate-700 text-white text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 resize-y"
            data-testid="broadcast-body-input"
          />

          {pendingImage && (
            <div className="flex items-center gap-2 text-xs text-slate-300">
              <img src={pendingImage.url} alt="" className="w-12 h-12 rounded object-cover" />
              <span>Image attached</span>
              <button
                type="button"
                onClick={() => setPendingImage(null)}
                className="ml-auto text-rose-400 hover:underline"
              >Remove</button>
            </div>
          )}

          <div className="flex items-center gap-2">
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={onPickImage}
              data-testid="broadcast-image-input"
            />
            <Button
              type="button"
              variant="outline"
              onClick={() => fileRef.current?.click()}
              className="border-slate-600 text-slate-200 hover:bg-slate-700"
              data-testid="broadcast-image-btn"
            >
              <ImageIcon className="w-4 h-4 mr-2" /> Attach image
            </Button>
            <Button
              type="button"
              onClick={sendBroadcast}
              disabled={sending}
              className="bg-pink-600 hover:bg-pink-700 text-white"
              data-testid="broadcast-send-btn"
            >
              {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Send broadcast
            </Button>
          </div>

          {recentBroadcasts.length > 0 && (
            <div className="pt-3 border-t border-slate-700">
              <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-2">
                Recent broadcasts
              </div>
              <ul className="space-y-1.5">
                {recentBroadcasts.map((b) => (
                  <li key={b.message_id} className="text-xs text-slate-400">
                    <span className="text-[10px] uppercase font-bold text-slate-500 mr-1.5">
                      [{b.broadcast_audience}]
                    </span>
                    <span className="text-slate-200 line-clamp-1">{b.body || '(image)'}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </CardContent>
      </Card>

      {/* DM picker */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-indigo-400" /> Direct message
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or email…"
              className="pl-9 bg-slate-900 border-slate-700"
              data-testid="admin-recipient-search"
            />
          </div>

          <div className="max-h-72 overflow-y-auto rounded-lg border border-slate-700/50 divide-y divide-slate-700/50">
            {searchLoading && (
              <div className="p-3 text-xs text-slate-400 flex items-center gap-2">
                <Loader2 className="w-3 h-3 animate-spin" /> Searching…
              </div>
            )}
            {!searchLoading && search && searchResults.length === 0 && (
              <div className="p-3 text-xs text-slate-500">No matches.</div>
            )}
            {!searchLoading && !search && (
              <div className="p-3 text-xs text-slate-500">
                Start typing to find a reader or writer.
              </div>
            )}
            {searchResults.map((r) => (
              <button
                key={r.user_id}
                type="button"
                onClick={() => openDMWith(r)}
                className="w-full text-left p-3 hover:bg-slate-700/40 transition-colors flex items-center gap-2.5"
                data-testid={`admin-recipient-${r.user_id}`}
              >
                {r.picture ? (
                  <img src={r.picture} alt="" className="w-8 h-8 rounded-full object-cover" />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-white truncate">
                    {r.name || (r.email || '').split('@')[0]}
                  </div>
                  <div className="text-[11px] text-slate-400 truncate">
                    {r.email} · {r.role}
                  </div>
                </div>
                <Send className="w-4 h-4 text-indigo-400 shrink-0" />
              </button>
            ))}
          </div>

          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/inbox')}
            className="w-full border-slate-600 text-slate-200 hover:bg-slate-700"
            data-testid="admin-open-inbox"
          >
            Open my full inbox →
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminMessagesTab;
